import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, ImagePlus, MessageCircle, PenLine, X } from "lucide-react";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";
import { reactionSummary, reactionTotal, storyDate } from "../lib/stories";

function StoryComposer({ onClose }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [cover, setCover] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const dialog = useRef(null);
  const navigate = useNavigate();
  const preview = useMemo(() => cover ? URL.createObjectURL(cover) : "", [cover]);

  useEffect(() => {
    dialog.current?.focus();
    return () => preview && URL.revokeObjectURL(preview);
  }, [preview]);

  useEffect(() => {
    const closeOnEscape = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  async function publish(event) {
    event.preventDefault();
    if (!cover) {
      setError("Choose a cover picture for your story.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const form = new FormData();
      form.set("title", title);
      form.set("body", body);
      form.set("cover", cover);
      const result = await api("/api/stories", { method: "POST", body: form });
      navigate(`/stories/${result.storyId}`);
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop story-composer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="story-composer" role="dialog" aria-modal="true" aria-labelledby="story-composer-title" tabIndex="-1" ref={dialog}>
        <header className="story-composer__header">
          <div>
            <p className="kicker">Add to the class memory</p>
            <h2 id="story-composer-title">Tell it in your own words.</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close story editor"><X aria-hidden="true" /></button>
        </header>
        <form className="story-composer__form" onSubmit={publish}>
          <div className="story-composer__fields">
            <label className="form-field">
              Story title
              <input required minLength="3" maxLength="140" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="The day the workshop went silent" />
              <small>{title.length} / 140</small>
            </label>
            <label className="form-field">
              Your memory
              <textarea required minLength="20" maxLength="20000" rows="14" value={body} onChange={(event) => setBody(event.target.value)} placeholder="Take us back to the place, the people, and the moment…" />
              <small>{body.length.toLocaleString()} / 20,000</small>
            </label>
          </div>
          <label className={preview ? "story-cover-picker has-preview" : "story-cover-picker"}>
            {preview ? <img src={preview} alt="Selected story cover preview" /> : <ImagePlus aria-hidden="true" />}
            <span>{preview ? "Choose a different cover" : "Choose a cover picture"}</span>
            <small>JPEG, PNG, or WebP · up to 8 MB</small>
            <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setCover(event.target.files?.[0] || null)} required />
          </label>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <footer className="story-composer__actions">
            <button className="text-link" type="button" onClick={onClose}>Keep it for later</button>
            <button className="button button--coral" disabled={saving} type="submit">{saving ? "Publishing…" : "Publish this story"} <ArrowRight aria-hidden="true" /></button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function StoryCard({ story, index }) {
  const reactions = reactionSummary(story.reactionCounts);
  return (
    <article className={index === 0 ? "story-card story-card--featured" : "story-card"}>
      <Link className="story-card__cover" to={`/stories/${story.id}`}>
        <img src={story.coverUrl} alt="" />
        <span className="story-card__number">{String(index + 1).padStart(2, "0")}</span>
      </Link>
      <div className="story-card__copy">
        <p className="story-card__byline">By {story.author.name} <span>·</span> {storyDate(story.createdAt)}</p>
        <h2><Link to={`/stories/${story.id}`}>{story.title}</Link></h2>
        <p className="story-card__excerpt">{story.excerpt}{story.excerpt.length >= 420 ? "…" : ""}</p>
        <div className="story-card__footer">
          <Link className="text-link" to={`/stories/${story.id}`}>Read the story <ArrowRight aria-hidden="true" /></Link>
          <div className="story-card__social" aria-label={`${reactionTotal(story.reactionCounts)} reactions and ${story.commentCount} comments`}>
            {reactions.length > 0 && <span>{reactions.map((reaction) => reaction.emoji).join(" ")} {reactionTotal(story.reactionCounts)}</span>}
            <span><MessageCircle aria-hidden="true" /> {story.commentCount}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Stories({ session, onLogin }) {
  const authenticated = Boolean(session?.authenticated);
  const canContribute = Number(session?.user?.memberId) > 0;
  const [composing, setComposing] = useState(false);
  const loader = useCallback(
    () => authenticated ? api("/api/stories") : Promise.resolve({ stories: [] }),
    [authenticated],
  );
  const { data, error, loading } = useApi(loader, [loader]);
  const stories = data?.stories || [];

  if (!authenticated) {
    return (
      <div className="stories-gate section-wrap">
        <MemberGate onSuccess={onLogin} />
      </div>
    );
  }

  return (
    <>
      <section className="stories-hero">
        <div className="stories-hero__copy">
          <p className="kicker">The stories we carried home</p>
          <h1>Thirty years.<br /><em>Still vivid.</em></h1>
          <p>Tell the stories that never made it into the yearbook—the ordinary afternoons, lasting friendships, small rebellions, and moments that shaped who we became.</p>
          {canContribute ? (
            <button className="button button--coral" type="button" onClick={() => setComposing(true)}><PenLine aria-hidden="true" /> Write your story</button>
          ) : (
            <Link className="button button--cream" to="/members">Use your individual account to contribute <ArrowRight aria-hidden="true" /></Link>
          )}
        </div>
        <div className="stories-hero__mark" aria-hidden="true">
          <span>“</span>
          <p>Memory is how a class stays together.</p>
          <small>MACE · 1991—1995</small>
        </div>
      </section>

      <section className="story-library section-wrap">
        <header className="story-library__heading">
          <div>
            <p className="kicker">Written by classmates</p>
            <h2>Our living archive</h2>
          </div>
          <span>{stories.length} {stories.length === 1 ? "story" : "stories"}</span>
        </header>
        {loading && <Loading label="Opening the story archive" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!loading && !error && stories.length === 0 && (
          <div className="stories-empty">
            <BookOpen aria-hidden="true" />
            <p className="kicker">The first page is waiting</p>
            <h2>Every class has a story only one person can tell.</h2>
            <p>Begin with a room, a face, a journey, or one college day you can still see clearly.</p>
            {canContribute && <button className="button button--ink" type="button" onClick={() => setComposing(true)}>Write the first story <PenLine aria-hidden="true" /></button>}
          </div>
        )}
        <div className="story-grid">
          {stories.map((story, index) => <StoryCard key={story.id} story={story} index={index} />)}
        </div>
      </section>

      {composing && <StoryComposer onClose={() => setComposing(false)} />}
    </>
  );
}
